import type {
  CSSProperties,
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  TouchEvent as ReactTouchEvent,
} from 'react'
import { useRef, useState } from 'react'
import { TableHead } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { useSortable } from '@dnd-kit/sortable'
import { EyeIcon, FunnelIcon, GearIcon, InfoIcon } from '@phosphor-icons/react'
import { flexRender, Header } from '@tanstack/react-table'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

import {
  ColumnDropPosition,
  ColumnFilterValue,
  ColumnMeta,
  NON_REORDERABLE_COLUMN_IDS,
} from '../types'
import { ColumnMenuPopover } from './ColumnMenuPopover'

interface TableHeaderProps<T> {
  header: Header<T, unknown>
  enableColumnResizing?: boolean
  enableColumnReordering?: boolean
  onColumnResize?: (columnId: string, width: number) => void
  onColumnResizeEnd?: (columnId: string, width: number) => void
  columnWidth?: number
  dropIndicator?: ColumnDropPosition | null
  isColumnDragging?: boolean
  // Column management props
  onHideColumn?: (columnId: string) => void
  onToggleSort?: (columnId: string) => void
  // Custom field editing props
  onEditField?: (customFieldDefinitionId: string) => void
  // Inline column rename (primary/system columns without a field definition)
  onRenameColumn?: (columnId: string, newLabel: string) => void
  // Column filtering props
  columnFilters?: Record<string, ColumnFilterValue>
  onColumnFilterChange?: (
    columnId: string,
    value: ColumnFilterValue | null,
  ) => void
  // Hide column menu (sort/filter/hide options)
  hideColumnMenu?: boolean
}

export function TableHeaderCell<T>({
  header,
  enableColumnResizing = false,
  enableColumnReordering = false,
  onColumnResize,
  onColumnResizeEnd,
  columnWidth,
  dropIndicator,
  isColumnDragging = false,
  // Column management props
  onHideColumn,
  onToggleSort,
  // Custom field editing props
  onEditField,
  // Inline column rename
  onRenameColumn,
  // Column filtering props
  columnFilters,
  onColumnFilterChange,
  // Hide column menu
  hideColumnMenu = false,
}: TableHeaderProps<T>) {
  const { column } = header
  const meta = column.columnDef.meta as ColumnMeta | undefined
  const [isResizing, setIsResizing] = useState(false)
  const resizeStartRef = useRef<{ startX: number; startWidth: number } | null>(
    null,
  )
  const resizeLatestWidthRef = useRef<number | null>(null)

  const isFirstColumn = column.getIndex() === 0
  const isViewColumn = column.id === 'view'
  const isCustomizeColumn = column.id === '_customize'
  const isSpacerColumn = column.id === 'spacer'
  const columnLabel =
    meta?.label ||
    (typeof header.column.columnDef.header === 'string'
      ? header.column.columnDef.header
      : column.id)

  const filterValue = columnFilters?.[column.id]
  const hasActiveFilter =
    filterValue &&
    filterValue.value !== null &&
    filterValue.value !== '' &&
    (!Array.isArray(filterValue.value) || filterValue.value.length > 0)

  const isDraggable =
    enableColumnReordering &&
    !isFirstColumn &&
    !NON_REORDERABLE_COLUMN_IDS.has(column.id) &&
    !meta?.isFixed

  const finalWidth = columnWidth ? `${columnWidth}px` : meta?.width || 'auto'

  const handleMouseDown = (e: ReactMouseEvent) => {
    if (!enableColumnResizing || !meta?.enableResizing || !onColumnResize) {
      return
    }

    e.preventDefault()
    e.stopPropagation()

    const headerElement = e.currentTarget.parentElement as HTMLElement
    const rect = headerElement.getBoundingClientRect()
    const startX = e.clientX
    const startWidth = rect.width

    resizeStartRef.current = { startX, startWidth }
    resizeLatestWidthRef.current = startWidth
    setIsResizing(true)
    document.body.classList.add('active-resize')

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!resizeStartRef.current) return

      const deltaX = moveEvent.clientX - resizeStartRef.current.startX
      const newWidth = Math.max(30, resizeStartRef.current.startWidth + deltaX)

      resizeLatestWidthRef.current = newWidth
      onColumnResize(column.id, newWidth)
    }

    const handleMouseUp = () => {
      const finalWidth = resizeLatestWidthRef.current
      setIsResizing(false)
      resizeStartRef.current = null
      resizeLatestWidthRef.current = null
      document.body.classList.remove('active-resize')
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      if (typeof finalWidth === 'number') {
        onColumnResizeEnd?.(column.id, finalWidth)
      }
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  const handleResizeMouseDown = (e: ReactMouseEvent) => {
    if (!enableColumnResizing || !meta?.enableResizing || !onColumnResize) {
      return
    }

    e.stopPropagation()
    handleMouseDown(e)
  }

  const handleNonDragPointerDown = (
    e: ReactPointerEvent | ReactMouseEvent | ReactTouchEvent,
  ) => {
    e.stopPropagation()
  }

  const { attributes, listeners, setNodeRef, isDragging } = useSortable({
    id: column.id,
    disabled: !isDraggable || isResizing,
  })

  const style = {
    transform: 'none',
    transition: 'none',
    '--column-width': finalWidth,
  } as CSSProperties

  const canSort = column.getCanSort()

  return (
    <TableHead
      ref={setNodeRef}
      style={style}
      className={cn(
        'group/column-header relative',
        meta?.className,
        isDraggable
          ? isColumnDragging
            ? 'cursor-grabbing'
            : 'cursor-grab'
          : canSort
            ? 'cursor-pointer'
            : undefined,
        isDragging && 'opacity-35',
        dropIndicator && 'bg-primary/[0.035]',
      )}
      data-column={column.id}
      data-fixed={isFirstColumn || isViewColumn}
      data-drop-indicator={dropIndicator ?? undefined}
      data-column-dragging={isDragging || undefined}
      {...(isDraggable ? { ...attributes, ...listeners } : {})}
    >
      <div className="flex min-w-0 items-center gap-1 pr-10">
        {header.isPlaceholder
          ? null
          : flexRender(header.column.columnDef.header, header.getContext())}
        {meta?.description && !header.isPlaceholder && (
          <span
            onPointerDownCapture={handleNonDragPointerDown}
            onMouseDownCapture={handleNonDragPointerDown}
            onTouchStartCapture={handleNonDragPointerDown}
          >
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={`About ${columnLabel}`}
                    className="rounded-sm text-muted-foreground/50 opacity-0 transition-opacity hover:text-muted-foreground group-hover/column-header:opacity-100 focus-visible:opacity-100"
                  >
                    <InfoIcon className="h-3 w-3" />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  align="start"
                  className="max-w-[260px] text-xs"
                >
                  {meta.description}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </span>
        )}
        {meta?.clientPortalVisibility && !header.isPlaceholder && (
          <span
            onPointerDownCapture={handleNonDragPointerDown}
            onMouseDownCapture={handleNonDragPointerDown}
            onTouchStartCapture={handleNonDragPointerDown}
          >
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    aria-label={
                      meta.clientPortalVisibility === 'edit'
                        ? `${columnLabel} can be edited by the client`
                        : `${columnLabel} is visible to the client`
                    }
                    className="rounded-sm text-muted-foreground/50 opacity-0 transition-opacity group-hover/column-header:opacity-100"
                  >
                    <EyeIcon className="h-3 w-3" />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="start" className="text-xs">
                  {meta.clientPortalVisibility === 'edit'
                    ? 'Client can edit'
                    : 'Visible to client'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </span>
        )}
      </div>

      {!hideColumnMenu &&
        !meta?.hideColumnMenu &&
        !isViewColumn &&
        !isCustomizeColumn &&
        !isSpacerColumn && (
          <div
            className="absolute right-1.5 top-1/2 z-10 -translate-y-1/2"
            onPointerDownCapture={handleNonDragPointerDown}
            onMouseDownCapture={handleNonDragPointerDown}
            onTouchStartCapture={handleNonDragPointerDown}
          >
            <ColumnMenuPopover
              columnId={column.id}
              columnLabel={columnLabel}
              canHide={!isFirstColumn && !meta?.isFixed}
              canSort={column.getCanSort()}
              canFilter={true}
              canEdit={!!meta?.customFieldDefinitionId}
              canRename={
                isFirstColumn && !meta?.customFieldDefinitionId && !!onRenameColumn
              }
              customFieldDefinitionId={meta?.customFieldDefinitionId}
              fieldType={meta?.fieldType}
              filterValue={filterValue}
              dropdownOptions={meta?.dropdownOptions}
              userOptions={meta?.userOptions}
              onHideColumn={onHideColumn}
              onToggleSort={onToggleSort}
              onEditField={onEditField}
              onRename={onRenameColumn}
              onFilterChange={onColumnFilterChange}
              triggerClassName={cn(
                'inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium',
                'h-7 w-7 rounded-md p-0 transition-all duration-200',
                'text-muted-foreground/60 hover:bg-accent/80 hover:text-foreground active:bg-accent',
                'focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                'disabled:pointer-events-none disabled:opacity-50',
                '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
                hasActiveFilter
                  ? 'opacity-100'
                  : 'pointer-events-none opacity-0 group-hover/column-header:pointer-events-auto group-hover/column-header:opacity-100',
                hasActiveFilter &&
                  'text-muted-foreground/80 hover:text-foreground',
              )}
              triggerTitle={
                hasActiveFilter
                  ? `Filter active on ${columnLabel}`
                  : `Column options for ${columnLabel}`
              }
            >
              {hasActiveFilter ? (
                <FunnelIcon weight="fill" className="h-3.5 w-3.5" />
              ) : (
                <GearIcon className="h-3.5 w-3.5" />
              )}
            </ColumnMenuPopover>
          </div>
        )}

      {enableColumnResizing &&
        meta?.enableResizing &&
        !isViewColumn &&
        !isCustomizeColumn &&
        !isSpacerColumn && (
          <div
            className={cn(
              'column-resizer absolute right-0 top-0 z-20 h-full w-2 cursor-col-resize opacity-0 transition-opacity',
              'bg-border hover:bg-foreground group-hover/column-header:opacity-100',
              isResizing && 'bg-foreground opacity-100',
            )}
            onPointerDownCapture={handleNonDragPointerDown}
            onTouchStartCapture={handleNonDragPointerDown}
            onMouseDown={handleResizeMouseDown}
            title="Resize column"
          />
        )}
    </TableHead>
  )
}

TableHeaderCell.displayName = 'DataTable.TableHeaderCell'
